import { useEffect, useReducer, useRef } from "react";
import ReconnectingWebSocket from "reconnecting-websocket";
import useErrorStatus from "../../hooks/UseErrorStatus";
import { ErrorTimeouts } from "../../constants/ErrorTimeouts";
import { useMap, useMapEvents } from "react-leaflet";
import DroneMarker from "./DroneMarker";
import { useSlideOverDialogContext } from "../../menus/SlideOverDialog/SlideOverDialogContext";
import { point, distance } from "@turf/turf";
import { TWS_WS_URL } from "../../environment/apis.config";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import { Location } from "../../interfaces/Location";

const MAX_HISTORY = 10_000;
const TARGET_PIXELS  = 35

function metersPerPixel(centerLat: number, zoom: number): number {
  return (
    156543.03392 *
    Math.cos((centerLat  * Math.PI) / 180) /
    Math.pow(2, zoom)
  );
}

function getDistanceInMeters(a: any, b: any): number {  
  const point1 = point(a.coordinates); 
  const point2 = point(b.coordinates);

  const meters = distance(point1, point2, { units: "meters" });

  return meters;
}

function buildPacketsForZoom(history:any, zoom: number, centerLat: number) {
  const threshold = TARGET_PIXELS * metersPerPixel(centerLat, zoom);
  const result = [];
  for (const packet of history) {
    const last = result[result.length - 1];
    if (!last || getDistanceInMeters(last.geometry, packet.geometry) >= threshold) {
      result.push(packet);
    }
  }
  return result;
}

const DroneData = () => {
  const notificationStatus = useErrorStatus();
  const map = useMap();
  const { setDroneSensors, visibleDroneSensors } = useSlideOverDialogContext();

  const historyRef = useRef<any[]>([]);

  const validRef = useRef<any[]>([]);

  const zoomRef = useRef<number>(map.getZoom());

  const zoomDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const sensorSetRef = useRef<Set<string>>(new Set());

  const [, forceRender] = useReducer((x: number) => x + 1, 0);

  const location: Location = useSelector(
    (state: RootState) => state.user.defaultLocation
  )
  
  const CONNECT_MESSAGE = JSON.stringify({
    message_type: "connect",
    dataFeed: "dronedata",
    filterType : "point",
    lat : Number(location.latitude),
    long : Number(location.longitude),
    radius : 100
  });

  //  WebSocket 
  useEffect(() => {
    const token = localStorage.getItem("token");
    const url = `${TWS_WS_URL}?token=${token}`;

    const ws = new ReconnectingWebSocket(url, [], {
      maxRetries: 10,
      maxReconnectionDelay: 5000,
    });

    notificationStatus.addMessage(
      "Waiting for data feed.",
      200,
      "info",
      false,
      ErrorTimeouts.GENERAL
    );

    ws.onopen = () => console.log("WebSocket connection established");
    ws.onclose = () => console.log("WebSocket connection closed");
    ws.onerror = (error) => console.error("WebSocket error:", error);

    ws.onmessage = (event) => {
      try {
        const parsedData = JSON.parse(event.data);
 
        // Handshake: server signals online → subscribe.
        if (
          parsedData.msg_type === "info" &&
          parsedData.connection_status === "online"
        ) {
          ws.send(CONNECT_MESSAGE);
          return;
        }

        if(parsedData.msg_type === "data"){
          // Append and cap        
          historyRef.current.push(parsedData);
          if (historyRef.current.length > MAX_HISTORY) {
            historyRef.current.shift();
          }

          // Add to the slideover dialog
          if (!sensorSetRef.current.has(parsedData.properties.SensorType.value)) {
            sensorSetRef.current.add(parsedData.properties.SensorType.value);
            setDroneSensors((prev: string[]) =>
              prev.includes(parsedData.properties.SensorType.value)
                ? prev
                : [...prev, parsedData.properties.SensorType.value]
            );
          }

          // Compare only against the last visible point at the current zoom.
          const threshold = TARGET_PIXELS * metersPerPixel(map.getCenter().lat, zoomRef.current);
          const last = validRef.current[validRef.current.length - 1];

          if (!last || getDistanceInMeters(last.geometry, parsedData.geometry) >= threshold) {
            validRef.current.push(parsedData); 
            forceRender();              
          }
        }
      } catch (err) {
        console.error("Failed to parse WebSocket message:", err);
      }
    };

    return () => {
      ws.close();
    };
  }, [location]);

  // ── Zoom change: debounced full replay ────────────────────────────────────
  useMapEvents({
    zoomend(e) {
      const newZoom: number = e.target.getZoom();

      // Update ref immediately so any packet arriving during the debounce
      // window already uses the new threshold.
      zoomRef.current = newZoom;

      // Cancel any pending replay triggered by a previous zoom event.
      if (zoomDebounceRef.current) clearTimeout(zoomDebounceRef.current);

      // Collapse rapid zoom bursts (pinch, scroll wheel) into one replay.
      zoomDebounceRef.current = setTimeout(() => {
        validRef.current = buildPacketsForZoom(historyRef.current, newZoom, map.getCenter().lat);
        forceRender();
      }, 150);
    },
  });

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      {validRef.current.map(
        (p) =>
          visibleDroneSensors.includes(p.properties.SensorType.value) && (
            <DroneMarker key={p.key} packet={p} />
          )
      )}
    </>
  );
};

export default DroneData;